import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '../../components/SettingsLayout';
import { ArrowBack, WorkspacePremium, CheckCircle } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { SUBSCRIPTION_PLAN_PRESETS, buildCreditsSummary, creditsToImages } from '../../lib/utils';

export const SubscriptionScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

  const currentPlanId: 'starter' | 'pro' | 'business' = 'pro';
  const currentPlan = SUBSCRIPTION_PLAN_PRESETS.find((plan) => plan.id === currentPlanId);
  const currentPlanLabelMap = {
    starter: t('pricing.planStarter'),
    pro: t('pricing.planPro'),
    business: t('pricing.planBusiness'),
  } as const;
  const planDescriptions = {
    starter: t('subscription.planStarterDesc'),
    pro: t('subscription.planProDesc'),
    business: t('subscription.planBusinessDesc'),
  } as const;

  const creditsLabel = t('pricing.creditsLabel');
  const imagesLabel = t('pricing.imagesLabel');
  const creditsMonthlySuffix = t('subscription.perMonth');

  const buildPlanCreditsSummary = (creditsMonthly: number | null) =>
    `${buildCreditsSummary(creditsMonthly ?? 0, creditsLabel, imagesLabel)} ${creditsMonthlySuffix}`;

  const buildPlanFeatures = (plan: (typeof SUBSCRIPTION_PLAN_PRESETS)[number]) => {
    const creditsSummary = buildPlanCreditsSummary(plan.creditsMonthly);

    switch (plan.id) {
      case 'starter':
        return [
          creditsSummary,
          t('subscription.featureStarter1'),
          t('subscription.featureStarter2'),
          t('subscription.featureStarter3'),
        ];
      case 'pro':
        return [
          creditsSummary,
          t('subscription.featurePro1'),
          t('subscription.featurePro2'),
          t('subscription.featurePro3'),
          t('subscription.featurePro4'),
        ];
      case 'business':
        return [
          creditsSummary,
          t('subscription.featureBusiness1'),
          t('subscription.featureBusiness2'),
          t('subscription.featureBusiness3'),
          t('subscription.featureBusiness4'),
        ];
      default:
        return [creditsSummary];
    }
  };

  const currentIndex = SUBSCRIPTION_PLAN_PRESETS.findIndex((plan) => plan.id === currentPlanId);
  const isYearly = billingCycle === 'yearly';
  const nextBillingDate = t('subscription.nextBillingDate');

  return (
    <SettingsLayout>
      <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/settings/billing')}
            className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-full"
          >
            <ArrowBack />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{t('subscription.title')}</h1>
            <p className="text-slate-500 text-sm mt-1">{t('subscription.subtitle')}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
              <WorkspacePremium className="text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-500">{t('subscription.currentPlanLabel')}:</span>
                <span className="text-base font-bold text-slate-900">{currentPlanLabelMap[currentPlanId]}</span>
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wider rounded border border-emerald-100">
                  {t('subscription.activeTag')}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {t('subscription.nextBilling')} <span className="font-semibold text-slate-700">{nextBillingDate}</span>
              </p>
              {currentPlan?.creditsMonthly && (
                <p className="text-xs text-slate-400 mt-1">
                  {currentPlan.creditsMonthly} {t('purchaseCredits.creditsLabel')} · {creditsToImages(currentPlan.creditsMonthly)} {t('purchaseCredits.imagesUnit')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('subscription.activeCycle')}:</span>
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                {isYearly ? t('subscription.yearly') : t('subscription.monthly')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center mb-10">
          <div className="bg-slate-100 p-1 rounded-full inline-flex items-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
                billingCycle === 'monthly' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subscription.monthly')}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('subscription.yearly')}
              <span className="bg-blue-100 text-blue-600 text-[10px] px-2 py-0.5 rounded-full font-bold">
                {t('subscription.yearlySave')}
              </span>
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-3 font-medium">
            {billingCycle === 'yearly' ? t('subscription.billedYearly') : t('subscription.billedMonthly')}
          </p>
          <p className="text-[11px] text-slate-400 mt-2">{t('subscription.rolloverHint')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-12">
          {SUBSCRIPTION_PLAN_PRESETS.map((plan, index) => {
            const isCurrent = plan.id === currentPlanId;
            const price = isYearly ? plan.yearlyUsd : plan.monthlyUsd;
            const period = isYearly ? t('subscription.perYear') : t('subscription.perMonth');
            const cardHighlight = isCurrent || plan.highlight;
            const actionLabel = isCurrent
              ? t('subscription.activePlan')
              : index < currentIndex
                ? t('subscription.downgrade')
                : t('subscription.upgrade');
            const buttonDisabled = isCurrent;

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl border ${cardHighlight ? 'border-2 border-primary shadow-lg' : 'border-slate-200 shadow-sm'} p-8 flex flex-col relative`}
              >
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg shadow-blue-200">
                    {t('subscription.currentPlanBadge')}
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-900">{currentPlanLabelMap[plan.id]}</h3>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">{planDescriptions[plan.id]}</p>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-900">${price}</span>
                    <span className="text-slate-400 font-medium">{period}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2 font-medium">
                    {billingCycle === 'yearly' ? t('subscription.billedYearly') : t('subscription.billedMonthly')}
                  </p>
                </div>
                <div className="flex-1 space-y-4 mb-8">
                  {buildPlanFeatures(plan).map((feature, featureIndex) => (
                    <div key={`${plan.id}-${featureIndex}`} className="flex items-start gap-3">
                      <CheckCircle className="text-emerald-500" style={{ fontSize: 20 }} />
                      <span className={`text-sm ${cardHighlight ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (!buttonDisabled) {
                      alert(`${actionLabel}...`);
                    }
                  }}
                  className={`w-full py-3 text-sm font-bold rounded-xl transition-all ${
                    buttonDisabled
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-white border border-slate-200 text-slate-900 hover:bg-slate-50 hover:border-slate-300'
                  }`}
                  disabled={buttonDisabled}
                >
                  {actionLabel}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-white rounded-2xl border border-slate-200 shadow-sm p-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <h4 className="text-xl font-bold text-slate-900">{t('subscription.customTitle')}</h4>
            <p className="text-slate-500 text-sm mt-1 max-w-md">{t('subscription.customDesc')}</p>
          </div>
          <button
            onClick={() => alert(t('subscription.consultSales'))}
            className="px-8 py-3 bg-primary text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 whitespace-nowrap active:scale-95"
          >
            {t('subscription.consultSales')}
          </button>
        </div>

        <div className="flex flex-col items-center gap-6 pt-12">
          <button
            onClick={() => {
              if (confirm(t('subscription.cancelConfirm'))) {
                alert(t('subscription.cancel'));
              }
            }}
            className="text-xs font-semibold text-slate-400 hover:text-rose-500 transition-colors uppercase tracking-widest"
          >
            {t('subscription.cancel')}
          </button>
        </div>

        <footer className="mt-20 text-center pb-12">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.1em]">{t('subscription.footer')}</p>
        </footer>
      </div>
    </SettingsLayout>
  );
};
